import React, { useState } from "react";
import { KTSVG } from "../../../helpers";

type Props = {
  className: string;
  innerPadding?: string;
};

const StepContent17: React.FC<Props> = ({ className, innerPadding = "" }) => {
  const [category, setCategory] = useState("1");
  const [option, setOption] = useState("1");
  const [fleet, setFleet] = useState("1");
  return (

      <div className={`card ${className}`}>
        {/* begin::Body */}
        <div className={`card-body ${innerPadding}`}>
          {/* begin::Title */}
          <div className="mb-10">
            <h3 className="fw-bold text-dark mb-2 fs-3">Calculate customer life-time value (LTV)</h3>
            <span className="fs-7 text-muted">
             Read each section and then fill in your answers at the bottom.
            </span>
          </div>
          {/* end::Title */}

          {/* begin::Section */}
          <div className="mb-10">
            <div className="accordion" id="kt_accordion_1">
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_1">
                  <button
                    className="accordion-button fs-7 fw-bold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_1"
                    aria-expanded="true"
                    aria-controls="kt_accordion_1_body_1"
                  >
                    Calculate customer life-time value (LTV)
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_1"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_1"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  In this step you will add up the revenue that you can expect to receive from an individual customer, then discount the revenue based on how much it will cost you to repay investors over time.

You will do the math or “unit economics” to discern whether it is a sustainable and attractive business from a microeconomic standpoint. The Lifetime Value of an Acquired Customer (LTV) calculation, along with the Cost of Customer Acquisition (COCA) calculation, will help you determine how profitable your business will be in the beachhead market.

The Lifetime Value of an Acquired Customer calculation is the profit that a new customer will provide on average, discounted to reflect the high cost of acquiring capital that a startup faces. It is important to be realistic, not optimistic, when calculating LTV, and to know the underlying drivers behind LTV so you can work to increase it. You will be comparing the LTV to COCA. An LTV:COCA ratio of 3:1 or higher is what you will be aiming for.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_2">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_2"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_2"
                  >
                    Collect key inputs
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_2"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_2"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  While the final number will likely be a range and not necessarily correct the first time you do it, it is very important to understand what drives the value of LTV. Here are the key inputs that you will need to understand to estimate the LTV:

One-time revenue stream, if any. Typically, if there is an up-front charge for your product, it is a one-time source of revenue.
Recurring revenue streams, if any. Subscription and maintenance fees, as well as repeated purchases of consumables, are all recurring revenues.
Additional revenue opportunities. If there are opportunities to “upsell” the customer, where the customer purchases additional products with minimal additional effort from your sales team, include these as revenue streams.
Gross margin for each of your revenue streams. The gross margin is the price of your product minus the production cost of making an individual product. Cost does not include sales and marketing costs (which is factored into the COCA) or overhead costs like R&D or administrative expenses.
Retention rate. For each recurring revenue stream, this rate is the percentage of customers who continue to pay the recurring fee for the product. Assume, for simplicity, that once the customer has stopped paying a recurring fee, the customer will no longer be receptive to upselling.
Life of product. For each one-time revenue stream, this is the length of time you expect the product to last before the customer will need to either purchase a replacement or discontinue use of the product.
Next product purchase rate. For each one-time revenue stream, this rate is the percentage of customers who will buy a replacement product from you when the current product has reached the end of its life.
Cost of capital rate for your business. Expressed as a yearly rate, this is how much it costs you, in debt or equity, to get money from investors for your business. For a new entrepreneur who lacks a track record and is just getting started, the appropriate number is most likely between 35 and 75 percent per year.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_3">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_3"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_3"
                  >
                    Calculate LTV
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_3"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_3"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  The LTV is the Net Present Value of your profits from year 0 through year 5. As a brand-new business, you will calculate the LTV over a five-year period. When projecting more than five years out, the compounded cost of capital for a startup is so high that it negates what value your customer provides you beyond five years. The customer still has value to you beyond five years, but you also have to factor your cost of capital rate into the calculation.

For each revenue stream, use the gross margin and the retention rate to calculate your profit per customer for the first year your customer buys the product from you ("Year 0"), as well as the subsequent five years. (Use the next product purchase rate instead of the retention rate for the years the customer would be expected to replace the product.)
Total the profit across all revenue streams for each year.
Calculate the Present Value at Above Cost of Capital, which discounts the profit to take into account that your investors will need to recoup with interest their investment in your business. The Present Value for year 0 is equal to that year's profits. To calculate the present value for each year's profits beyond year 0, use the following formula: Present Value = Profit × (1 − Cost of Capital Rate)t where t = number of years after year 0.
This is the LTV in dollars per customer.
Evaluate if your LTV is at least three times bigger than the estimated Cost of Customer Acquisition (COCA; this will be calculated in more detail in a later step). This is a sound rule of thumb for an attractive business, according to venture capitalist David Skok.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_4">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_4"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_4"
                  >
                    Consider secondary factors
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_4"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_4"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  There are many other secondary factors to consider when determining the Lifetime Value of your customer. Even if your LTV is too low for your product to be viable you should consider whether these elements are correct first; then consider whether you can positively impact your LTV with some adjustments. Make sure you think about all of these:

The business model. Your choice of business model can greatly affect your LTV and the amount of revenue you earn. Recurring revenue models such as subscription models often increase revenue, but require additional capital from investors up front, and thus have a very high cost of capital. A one-time charge up front can reduce the amount of capital you need to get started, but is not as lucrative on an ongoing basis.
LTV is about profit, not revenue. Your gross margin and cost of capital rates are integral to determining an accurate LTV. The most common mistake entrepreneurs make on LTV calculations is they simply tally up the revenue streams; but it is the profit that matters.
Overhead costs aren't negligible. To simplify the LTV calculation, overhead is excluded; but to account for this, the LTV must be substantially higher than the COCA. These overhead costs, which may include R&D and administrative expenses, are not included when determining the gross margin of a product.
Gross margins make a big difference. Wrapping your lower-margin core product with high-margin add-on products will substantially help your LTV.
Retention rates are very important. The longer you can keep a customer, the better your LTV becomes. This is one of a few levers you can easily control to improve the profitability of your business. A small increase in customer retention rates will mean significant improvements in your cumulative profits.
Finding additional real upselling opportunities can be very attractive. Upselling additional products to your customer can significantly improve your profit. Make sure to drive upselling based on the needs of your Persona, not just to improve your numbers. Companies that over-upsell can lose track of what value they are creating for their customer and also lose the trust and confidence of the customer.
                  </div>
                </div>
              </div>

            </div>
          </div>
          {/* end::Section */}

        </div>
        {/* end: Card Body */}
      </div>

  );
};

export { StepContent17 };
